import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneOff, Mic, MicOff, Video as VideoIcon, VideoOff, Users, Eye, Heart, ArrowLeft, Maximize2, Gift, Coins, Target, X } from "lucide-react";
import { WS_BASE, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TipGoalBar, GoalCelebration, TipGoalEditor, useCelebration } from "@/components/TipGoal";

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

const META = {
  group: { title: "Group Lounge", icon: Users, sub: "Everyone on camera together", camOn: true },
  watch: { title: "Watch Lounge", icon: Eye, sub: "Watch performers · go on camera anytime", camOn: false },
  one_on_one: { title: "1-on-1 Lounge", icon: Heart, sub: "Pick someone for a private session", camOn: true },
};

function Tile({ stream, label, onFocus }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#121212] border border-[#2A2A2A] group">
      <video ref={ref} autoPlay playsInline className="w-full h-full object-cover" />
      <span className="absolute bottom-2 left-2 text-xs px-2 py-0.5 rounded-full bg-black/60 text-white">{label}</span>
      {onFocus && (
        <button onClick={onFocus} className="absolute top-2 right-2 bg-black/60 text-[#D4AF37] p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 size={14} />
        </button>
      )}
    </div>
  );
}

export default function Lounge() {
  const { type } = useParams();
  const meta = META[type] || META.group;
  const { user } = useAuth();
  const navigate = useNavigate();
  const myId = user?.id;

  const localRef = useRef(null);
  const localStream = useRef(null);
  const wsRef = useRef(null);
  const peers = useRef({});
  const namesRef = useRef({});

  const [remotes, setRemotes] = useState({}); // id -> stream
  const [roster, setRoster] = useState([]);
  const [status, setStatus] = useState("Connecting…");
  const [micOn, setMicOn] = useState(meta.camOn);
  const [camOn, setCamOn] = useState(meta.camOn);
  const [pairedId, setPairedId] = useState(null); // one_on_one
  const [invite, setInvite] = useState(null); // incoming {from, name}
  const [focusId, setFocusId] = useState(null); // watch focus
  const [goals, setGoals] = useState({});
  const [goalsTick, setGoalsTick] = useState(0);
  const [goalOpen, setGoalOpen] = useState(false);
  const [celebrate, fireCelebration] = useCelebration();

  const send = useCallback((obj) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);
  const setRemote = (id, s) => setRemotes((p) => ({ ...p, [id]: s }));
  const dropRemote = (id) => setRemotes((p) => { const n = { ...p }; delete n[id]; return n; });

  // ---- Virtual gifts ----
  const [giftCatalog, setGiftCatalog] = useState([]);
  const [coins, setCoins] = useState(0);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null);
  const [sendingGift, setSendingGift] = useState(false);
  const [giftFx, setGiftFx] = useState([]);
  const pushGiftFx = useCallback((g) => {
    const id = `${Date.now()}-${Math.random()}`;
    setGiftFx((p) => [...p, { id, ...g }]);
    setTimeout(() => setGiftFx((p) => p.filter((x) => x.id !== id)), 2600);
  }, []);

  useEffect(() => {
    if (!myId) return;
    let mounted = true;

    const createPeer = (peerId, initiator) => {
      const pc = new RTCPeerConnection(ICE);
      localStream.current.getTracks().forEach((t) => pc.addTrack(t, localStream.current));
      pc.onicecandidate = (e) => { if (e.candidate) send({ type: "candidate", to: peerId, candidate: e.candidate }); };
      pc.ontrack = (e) => { setRemote(peerId, e.streams[0]); setStatus("Live"); };
      pc.onconnectionstatechange = () => {
        if (["failed", "closed", "disconnected"].includes(pc.connectionState)) dropRemote(peerId);
      };
      peers.current[peerId] = pc;
      if (initiator) {
        pc.createOffer().then((o) => pc.setLocalDescription(o))
          .then(() => send({ type: "offer", to: peerId, sdp: pc.localDescription }));
      }
      return pc;
    };
    const connectTo = (peerId) => {
      if (peerId === myId || peers.current[peerId]) return;
      createPeer(peerId, myId > peerId);
    };
    const closePeer = (peerId) => {
      peers.current[peerId]?.close();
      delete peers.current[peerId];
      dropRemote(peerId);
    };

    const start = async () => {
      let stream;
      try { stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }); }
      catch (e) { setStatus("Camera/mic permission denied"); return; }
      if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
      localStream.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;
      // Watch lounge: start as a viewer (camera + mic off until you choose to perform)
      if (!meta.camOn) {
        stream.getVideoTracks().forEach((t) => (t.enabled = false));
        stream.getAudioTracks().forEach((t) => (t.enabled = false));
      }

      const ws = new WebSocket(`${WS_BASE}/lounge/${type}?token=${localStorage.getItem("pe_token")}`);
      wsRef.current = ws;
      ws.onopen = () => setStatus(type === "one_on_one" ? "Pick someone to connect" : "Waiting for others…");
      ws.onmessage = async (evt) => {
        const msg = JSON.parse(evt.data);
        if (msg.type === "roster") {
          msg.members.forEach((m) => { namesRef.current[m.id] = m.name; });
          setRoster(msg.members.filter((m) => m.id !== myId));
          if (type === "group" || type === "watch") {
            const ids = new Set(msg.members.map((m) => m.id));
            msg.members.forEach((m) => { if (m.id !== myId) connectTo(m.id); });
            Object.keys(peers.current).forEach((pid) => { if (!ids.has(pid)) closePeer(pid); });
          }
          return;
        }
        if (msg.type === "gift") {
          pushGiftFx({ emoji: msg.gift?.emoji, fromName: msg.fromName, toName: msg.toName });
          setGoalsTick((t) => t + 1);
          if (msg.goalReached) fireCelebration(msg.toName, msg.goalTitle);
          return;
        }
        if (msg.from === myId) return;
        if (msg.type === "invite") { setInvite({ from: msg.from, name: msg.name }); return; }
        if (msg.type === "accept") { if (msg.to === myId) { setPairedId(msg.from); connectTo(msg.from); } return; }
        if (msg.type === "decline") { if (msg.to === myId) toast.error("They're not available right now"); return; }
        if (msg.type === "end") { closePeer(msg.from); setPairedId(null); return; }
        // WebRTC signaling (directed)
        if (msg.to !== myId) return;
        if (msg.type === "offer") {
          const pc = peers.current[msg.from] || createPeer(msg.from, false);
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          send({ type: "answer", to: msg.from, sdp: pc.localDescription });
        } else if (msg.type === "answer") {
          await peers.current[msg.from]?.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        } else if (msg.type === "candidate") {
          try { await peers.current[msg.from]?.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) {}
        }
      };
    };
    start();
    return () => {
      mounted = false;
      wsRef.current?.close();
      Object.values(peers.current).forEach((pc) => pc.close());
      peers.current = {};
      localStream.current?.getTracks().forEach((t) => t.stop());
    };
  }, [type, myId, send, meta.camOn, pushGiftFx, fireCelebration]);

  // Live tip goals for everyone in the room (including me)
  useEffect(() => {
    const ids = [myId, ...roster.map((m) => m.id)].filter(Boolean).join(",");
    if (!ids) return;
    api.get("/tip-goals", { params: { ids } }).then((r) => setGoals(r.data)).catch(() => {});
  }, [roster, goalsTick, myId]);

  // Load gift catalog + coin balance
  useEffect(() => {
    api.get("/gifts/catalog").then((r) => { setGiftCatalog(r.data.gifts || []); setCoins(r.data.coins || 0); }).catch(() => {});
  }, []);

  const sendGift = async (giftKey) => {
    const recipientId = type === "one_on_one" ? pairedId : (giftTarget || roster[0]?.id);
    if (!recipientId) { toast.error("No one here to gift yet"); return; }
    setSendingGift(true);
    try {
      const { data } = await api.post("/gifts/send", { recipient_id: recipientId, gift: giftKey, room: type });
      setCoins(data.coins);
      const toName = namesRef.current[recipientId] || "Member";
      const goalReached = !!data.goal?.reached;
      send({ type: "gift", gift: data.gift, fromName: user?.name, toName, goalReached, goalTitle: data.goal?.goal?.title, ...(type === "one_on_one" ? { to: recipientId } : {}) });
      pushGiftFx({ emoji: data.gift.emoji, fromName: user?.name, toName });
      setGoalsTick((t) => t + 1);
      if (goalReached) fireCelebration(toName, data.goal.goal.title);
      toast.success(`You sent a ${data.gift.label} ${data.gift.emoji}`);
      setGiftOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send gift");
    } finally { setSendingGift(false); }
  };

  const toggleMic = () => { const t = localStream.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); } };
  const toggleCam = () => { const t = localStream.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); } };
  const leave = () => {
    wsRef.current?.close();
    Object.values(peers.current).forEach((pc) => pc.close());
    localStream.current?.getTracks().forEach((t) => t.stop());
    navigate("/events");
  };

  // one_on_one actions
  const invitePerson = (id) => { send({ type: "invite", to: id, name: user?.name }); toast.success("Invite sent"); };
  const acceptInvite = () => { send({ type: "accept", to: invite.from }); setPairedId(invite.from); const pid = invite.from; setInvite(null); if (!peers.current[pid]) { /* connectTo runs via effect closure */ } connectViaAccept(pid); };
  const connectViaAccept = (pid) => {
    // deterministic initiator handled inside effect's connectTo; replicate minimal here
    if (!peers.current[pid] && localStream.current) {
      const pc = new RTCPeerConnection(ICE);
      localStream.current.getTracks().forEach((t) => pc.addTrack(t, localStream.current));
      pc.onicecandidate = (e) => { if (e.candidate) send({ type: "candidate", to: pid, candidate: e.candidate }); };
      pc.ontrack = (e) => setRemote(pid, e.streams[0]);
      peers.current[pid] = pc;
      if (myId > pid) pc.createOffer().then((o) => pc.setLocalDescription(o)).then(() => send({ type: "offer", to: pid, sdp: pc.localDescription }));
    }
  };
  const declineInvite = () => { send({ type: "decline", to: invite.from }); setInvite(null); };
  const endOneOnOne = () => { if (pairedId) { send({ type: "end", to: pairedId }); peers.current[pairedId]?.close(); delete peers.current[pairedId]; dropRemote(pairedId); setPairedId(null); } };

  const Icon = meta.icon;
  const remoteIds = Object.keys(remotes);

  // ---- 1-on-1 lobby (not yet paired) ----
  const showLobby = type === "one_on_one" && !pairedId;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <header className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <button data-testid="lounge-back" onClick={leave} className="text-[#A0A0A0] hover:text-white"><ArrowLeft size={20} /></button>
          <div>
            <div className="overline flex items-center gap-1"><Icon size={12} /> VIP Lounge</div>
            <div className="font-heading text-2xl gold-text">{meta.title}</div>
          </div>
        </div>
        <div className="text-sm text-[#A0A0A0]">{roster.length + 1} here · <span className="text-[#D4AF37]">{status}</span></div>
      </header>

      {Object.keys(goals).length > 0 && (
        <div data-testid="lounge-goals" className="px-4 pt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(goals).map(([gid, g]) => (
            <TipGoalBar key={gid} goal={g} compact testid={`lounge-goal-${gid}`} name={gid === myId ? "Your goal" : namesRef.current[gid]} />
          ))}
        </div>
      )}
      <GoalCelebration celebrate={celebrate} />
      {goalOpen && (
        <div className="absolute bottom-24 inset-x-0 z-50 flex justify-center px-4 pointer-events-none">
        <div className="pe-card rounded-2xl p-5 w-full max-w-[440px] pointer-events-auto" data-testid="lounge-goal-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-lg flex items-center gap-2"><Target size={16} className="text-[#D4AF37]" /> Your tip goal</div>
            <button data-testid="lounge-goal-close" onClick={() => setGoalOpen(false)} className="text-[#A0A0A0] hover:text-white"><X size={18} /></button>
          </div>
          <TipGoalEditor goal={goals[myId] || null} onSaved={() => setGoalsTick((t) => t + 1)} />
        </div>
        </div>
      )}

      {/* Incoming 1-on-1 invite */}
      {invite && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 pe-card rounded-2xl p-4 text-center" data-testid="incoming-invite">
          <div className="text-white mb-3"><span className="gold-text font-semibold">{invite.name}</span> wants a 1-on-1 with you</div>
          <div className="flex gap-2 justify-center">
            <button data-testid="accept-invite" onClick={acceptInvite} className="btn-gold px-5 py-2 rounded-full text-sm">Accept</button>
            <button data-testid="decline-invite" onClick={declineInvite} className="btn-outline-gold px-5 py-2 rounded-full text-sm">Decline</button>
          </div>
        </div>
      )}

      {showLobby ? (
        <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto w-full">
          <p className="text-sm text-[#A0A0A0] mb-4">{meta.sub}. Members available now:</p>
          {roster.length === 0 ? (
            <div className="pe-card rounded-2xl p-8 text-center text-[#A0A0A0]">No one else is here yet. Invite a friend to join the 1-on-1 lounge!</div>
          ) : (
            <div className="space-y-3">
              {roster.map((m) => (
                <div key={m.id} data-testid={`lobby-${m.id}`} className="pe-card rounded-2xl p-3 flex items-center gap-3">
                  <img src={m.photo || "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3"} alt="" className="w-12 h-12 rounded-full object-cover border border-[#D4AF37]/40" />
                  <div className="flex-1 font-heading text-lg">{m.name}</div>
                  <button data-testid={`invite-${m.id}`} onClick={() => invitePerson(m.id)} className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1"><Heart size={14} /> Connect</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : focusId && remotes[focusId] ? (
        <div className="flex-1 relative p-4">
          <Tile stream={remotes[focusId]} label={namesRef.current[focusId] || "Performer"} />
          <button onClick={() => setFocusId(null)} className="absolute top-6 right-6 btn-outline-gold px-4 py-2 rounded-full text-sm">Back to grid</button>
        </div>
      ) : (
        <div className={`flex-1 grid gap-3 p-4 overflow-y-auto ${remoteIds.length <= 0 ? "grid-cols-1" : remoteIds.length <= 3 ? "grid-cols-2" : "grid-cols-3"}`}>
          <div className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/60 bg-[#121212]" data-testid="local-tile">
            <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            <span className="absolute bottom-2 left-2 text-xs px-2 py-0.5 rounded-full bg-black/60 text-white">You</span>
          </div>
          {remoteIds.map((id) => (
            <Tile key={id} stream={remotes[id]} label={namesRef.current[id] || "Member"}
              onFocus={type === "watch" ? () => setFocusId(id) : undefined} />
          ))}
        </div>
      )}

      {/* Controls */}
      {!showLobby && (
        <div className="flex items-center justify-center gap-4 py-4 border-t border-[#2A2A2A]">
          <button data-testid="toggle-mic" onClick={toggleMic} className={`w-12 h-12 rounded-full flex items-center justify-center ${micOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
            {micOn ? <Mic size={20} /> : <MicOff size={20} />}
          </button>
          <button data-testid="toggle-cam" onClick={toggleCam} className={`w-12 h-12 rounded-full flex items-center justify-center ${camOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
            {camOn ? <VideoIcon size={20} /> : <VideoOff size={20} />}
          </button>
          {type === "one_on_one" && pairedId && (
            <button data-testid="end-1on1" onClick={endOneOnOne} className="btn-outline-gold px-5 h-12 rounded-full text-sm">End session</button>
          )}
          {(type !== "one_on_one" || pairedId) && (
            <button data-testid="open-gifts" onClick={() => setGiftOpen((o) => !o)}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25 transition-colors">
              <Gift size={20} />
            </button>
          )}
          {user?.is_creator && (
            <button data-testid="open-goal-editor" onClick={() => setGoalOpen((o) => !o)} title="Tip goal"
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25 transition-colors">
              <Target size={20} />
            </button>
          )}
          <button data-testid="leave-lounge" onClick={leave} className="w-14 h-14 rounded-full bg-[#E63946] text-white flex items-center justify-center hover:brightness-110">
            <PhoneOff size={24} />
          </button>
        </div>
      )}

      {/* Gift panel */}
      <AnimatePresence>
        {giftOpen && !showLobby && (
          <motion.div data-testid="gift-panel"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 z-50 pe-card rounded-2xl p-4 w-[92%] max-w-md">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading text-lg">Send a gift</div>
              <div className="text-xs text-[#D4AF37] flex items-center gap-1"><Coins size={13} /> {coins} coins</div>
            </div>
            {type !== "one_on_one" && roster.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] text-[#A0A0A0] mb-1">To</div>
                <div className="flex flex-wrap gap-2">
                  {roster.map((m) => (
                    <button key={m.id} data-testid={`gift-target-${m.id}`} onClick={() => setGiftTarget(m.id)}
                      className={`text-xs px-3 py-1 rounded-full border ${((giftTarget || roster[0]?.id) === m.id) ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A0A0A0]"}`}>
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 gap-2">
              {giftCatalog.map((g) => (
                <button key={g.key} data-testid={`gift-${g.key}`} disabled={sendingGift || coins < g.coins}
                  onClick={() => sendGift(g.key)}
                  className="flex flex-col items-center gap-1 p-3 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#D4AF37]/50 disabled:opacity-40 transition-colors">
                  <span className="text-2xl">{g.emoji}</span>
                  <span className="text-[10px] text-white">{g.label}</span>
                  <span className="text-[10px] text-[#D4AF37]">{g.coins}</span>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-[#A0A0A0] mt-3 text-center">Need more coins? Invite friends or top up in your wallet.</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating gift animations */}
      <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden">
        <AnimatePresence>
          {giftFx.map((g) => (
            <motion.div key={g.id}
              initial={{ opacity: 0, y: 40, scale: 0.5 }}
              animate={{ opacity: 1, y: -280, scale: 1.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.4, ease: "easeOut" }}
              className="absolute left-1/2 bottom-40 -translate-x-1/2 text-center">
              <div className="text-6xl drop-shadow-[0_0_20px_rgba(212,175,55,0.6)]">{g.emoji}</div>
              <div className="text-xs text-[#D4AF37] mt-1 whitespace-nowrap">{g.fromName} → {g.toName}</div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {type === "watch" && !showLobby && (
        <div className="text-center text-xs text-[#A0A0A0] pb-3">Camera is {camOn ? "ON — you're performing 🔥" : "off — you're watching. Tap the camera to go live."}</div>
      )}
    </div>
  );
}
